import React, { useState, useEffect } from 'react';
import './CalendarView.css';
import API_BASE_URL from './config';
import { getStatusText } from './components/DataTable/tableUtils';
import { normalizeStatus } from './constants';

function CalendarView({ onBack, onJobSelect, theme, toggleTheme }) {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [rfqs, setRfqs] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Fetch all RFQs
        fetch(`${API_BASE_URL}/rfqs`, {
            credentials: 'include'
        })
            .then(res => res.json())
            .then(data => {
                setRfqs(data);
                setLoading(false);
            })
            .catch(err => {
                console.error('Error fetching RFQs:', err);
                setLoading(false);
            });
    }, []);

    // Helper to parse dates safely
    const parseDateSafely = (dateStr) => {
        if (!dateStr) return null;
        
        // Try parsing as ISO date first
        let date = new Date(dateStr);
        if (!isNaN(date.getTime())) return date;
        
        // Try MM/DD/YYYY format
        const parts = dateStr.split('/');
        if (parts.length === 3) {
            date = new Date(parts[2], parts[0] - 1, parts[1]);
            if (!isNaN(date.getTime())) return date;
        }
        
        return null;
    };

    // Get status color - mirrors the canonical JOB_STATUSES badge colors in constants.js
    // (legacy user_status values are normalized first so old rows get their new bucket's color).
    const getStatusColor = (status) => {
        if (status === 'completed') return '#17a2b8'; // legacy-only display state, pending manual reassignment
        const colors = {
            'new': '#28a745',        // green
            'consider': '#fd7e14',   // orange
            'pursuing': '#ffc107',   // yellow
            'watch': '#007bff',      // blue
            'no_go': '#dc3545',      // red
            'won': '#28a745',        // bright green
            'lost': '#343a40',       // dark
            'irrelevant': '#6c757d', // gray
        };
        return colors[normalizeStatus(status)] || '#6c757d';
    };

    // Get calendar days for current month
    const getCalendarDays = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();
        
        const days = [];
        
        // Add empty cells for days before month starts
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push({ date: null, jobs: [] });
        }
        
        // Add days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const dateStr = date.toISOString().split('T')[0];
            
            // Find jobs due on this date
            const jobsOnDate = rfqs.filter(rfq => {
                const dueDate = parseDateSafely(rfq.due_date);
                if (!dueDate) return false;
                return dueDate.toISOString().split('T')[0] === dateStr;
            });
            
            days.push({ date, jobs: jobsOnDate });
        }
        
        return days;
    };

    // Navigate months
    const previousMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
        setSelectedDate(null);
    };

    const nextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
        setSelectedDate(null);
    };

    const goToToday = () => {
        setCurrentDate(new Date());
        setSelectedDate(null);
    };

    // Handle date click
    const handleDateClick = (date, jobs) => {
        if (!date || jobs.length === 0) return;
        setSelectedDate({ date, jobs });
    };

    const calendarDays = getCalendarDays();
    const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

    if (loading) {
        return (
            <div className="container mt-4">
                <div className="text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading calendar...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid mt-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <button className="btn btn-outline-secondary me-2" onClick={onBack}>
                        ← Back to RFQs
                    </button>
                    <h2 className="d-inline-block mb-0">📅 Calendar View</h2>
                </div>
                <button 
                    onClick={toggleTheme} 
                    className="theme-toggle"
                    title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
                    aria-label="Toggle theme"
                >
                    {theme === 'light' ? '🌙' : '☀️'}
                </button>
            </div>

            {/* Legend */}
            <div className="calendar-legend mb-4">
                <strong>Status Colors:</strong>
                <span className="legend-item">
                    <span className="legend-dot" style={{ backgroundColor: '#007bff' }}></span> New
                </span>
                <span className="legend-item">
                    <span className="legend-dot" style={{ backgroundColor: '#28a745' }}></span> Pursuing
                </span>
                <span className="legend-item">
                    <span className="legend-dot" style={{ backgroundColor: '#fd7e14' }}></span> Watch
                </span>
                <span className="legend-item">
                    <span className="legend-dot" style={{ backgroundColor: '#17a2b8' }}></span> Completed
                </span>
                <span className="legend-item">
                    <span className="legend-dot" style={{ backgroundColor: '#6c757d' }}></span> Lost
                </span>
                <span className="legend-item">
                    <span className="legend-dot" style={{ backgroundColor: '#adb5bd' }}></span> Declined
                </span>
                <span className="legend-item">
                    <span className="legend-dot" style={{ backgroundColor: '#dc3545' }}></span> Ignored
                </span>
            </div>

            {/* Calendar Navigation */}
            <div className="calendar-nav mb-4">
                <button className="btn btn-outline-primary" onClick={previousMonth}>
                    ← Previous
                </button>
                <h3 className="mx-3 mb-0">{monthName}</h3>
                <button className="btn btn-outline-primary" onClick={nextMonth}>
                    Next →
                </button>
                <button className="btn btn-primary ms-3" onClick={goToToday}>
                    Today
                </button>
            </div>

            {/* Calendar Grid */}
            <div className="calendar-grid">
                {/* Day headers */}
                <div className="calendar-header">Sunday</div>
                <div className="calendar-header">Monday</div>
                <div className="calendar-header">Tuesday</div>
                <div className="calendar-header">Wednesday</div>
                <div className="calendar-header">Thursday</div>
                <div className="calendar-header">Friday</div>
                <div className="calendar-header">Saturday</div>

                {/* Calendar days */}
                {calendarDays.map((day, idx) => (
                    <div
                        key={idx}
                        className={`calendar-day ${day.date ? 'has-date' : 'empty'} ${
                            day.jobs.length > 0 ? 'has-jobs' : ''
                        } ${
                            day.date && 
                            day.date.toDateString() === new Date().toDateString() 
                                ? 'today' 
                                : ''
                        }`}
                        onClick={() => handleDateClick(day.date, day.jobs)}
                    >
                        {day.date && (
                            <>
                                <div className="day-number">{day.date.getDate()}</div>
                                {day.jobs.length > 0 && (
                                    <div className="job-list">
                                        {day.jobs.map((job, jobIdx) => (
                                            <div
                                                key={jobIdx}
                                                className="job-item"
                                                title={`${job.organization} - ${job.title}`}
                                            >
                                                <div
                                                    className="job-dot"
                                                    style={{ backgroundColor: getStatusColor(job.user_status) }}
                                                />
                                                <span className="job-title-preview">
                                                    {job.title.length > 25 ? job.title.substring(0, 25) + '...' : job.title}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                ))}
            </div>

            {/* Selected Date Modal */}
            {selectedDate && (
                <div className="modal-overlay" onClick={() => setSelectedDate(null)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h4>
                                📅 {selectedDate.date.toLocaleDateString('en-US', { 
                                    weekday: 'long', 
                                    month: 'long', 
                                    day: 'numeric', 
                                    year: 'numeric' 
                                })}
                            </h4>
                            <button 
                                className="btn-close" 
                                onClick={() => setSelectedDate(null)}
                            ></button>
                        </div>
                        <div className="modal-body">
                            <p><strong>{selectedDate.jobs.length} RFQ(s) due:</strong></p>
                            <div className="jobs-list">
                                {selectedDate.jobs.map((job) => (
                                    <div key={job.job_id} className="job-card" onClick={() => {
                                        setSelectedDate(null);
                                        onJobSelect(job.job_id);
                                    }}>
                                        <div className="job-card-header">
                                            <span 
                                                className="status-badge"
                                                style={{ backgroundColor: getStatusColor(job.user_status) }}
                                            >
                                                {getStatusText(job.user_status || 'new')}
                                            </span>
                                            <span className="work-type">{job.work_type || 'Unknown'}</span>
                                        </div>
                                        <h5>{job.title}</h5>
                                        <p className="organization">{job.organization}</p>
                                        <p className="rfp-number">RFP: {job.rfp_number}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CalendarView;

